import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { IconButton, Menu, MenuItem } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import {
  handleFetchDatasets,
  handleDeleteDataset,
  handleDownloadError,
  fetchDataSource,
} from "../../modules/datasetModule.ts";
import DeleteConfirmationModal from "../../components/common/DeleteConfirmModal.tsx";
import Button from "../../components/common/Button.tsx";
import ReusableTable from "../../components/common/Table.tsx";
import {
  type MRT_ColumnDef,
  type MRT_PaginationState,
} from "material-react-table";
import {toast, ToastContainer} from 'react-toastify';import DatasourceModal from "./DatasourceModal.tsx";
import BreadCrumb from "../../components/common/BreadCrumb.tsx";
import { MoreHoriz, MoreVert } from "@mui/icons-material";
import { RiDeleteBin5Line, RiEyeLine } from "react-icons/ri";
import StatusBadge from "../../components/common/Badge.tsx";

interface Dataset {
  error_report: any;
  id: number;
  type: string;
  dataset_name: string;
  template_name: string;
  file_name: string;
  created_at: string;
  modified_at: string;
  total_fields: number;
  total_records: number;
  status: string;
  file_count: number;
  usage: string;
  iceberg_table_name: string;
  data_source: string;
  is_valid: boolean;
}

const DataList = () => {
  const navigate = useNavigate();
  //const toast = useToast();

  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalRows, setTotalRows] = useState(0);
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });
  const [menuAnchor, setMenuAnchor] = useState<{
    [key: number]: HTMLElement | null;
  }>({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<Dataset | null>(null);
  const [isDatasourceModalOpen, setIsDatasourceModalOpen] = useState(false);
  const [datasource, setDatasource] = useState([]);

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return "";
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return "";
    return date.toLocaleDateString("en-CA");
  };
  const handleUploadFiles = (rowData: Dataset) => {
    navigate(`/fileUpload/${rowData.id}`, {
      state: {
        templateName: rowData.template_name,
        datasetName: rowData.dataset_name,
        usage: rowData.usage,
      },
    });
  };

  useEffect(() => {
    const getDatasets = async () => {
      setLoading(true);
      try {
        const skip = pagination.pageIndex * pagination.pageSize;
        const limit = pagination.pageSize;
        const page = pagination.pageIndex + 1;

        const response = await handleFetchDatasets(skip, limit, page);

        let datasetsData: Dataset[] = [];
        let totalCount = 0;

        if (Array.isArray(response)) {
          datasetsData = response;
          totalCount = response.length;
        } else if (
          response &&
          typeof response === "object" &&
          "data" in response &&
          "total" in response
        ) {
          datasetsData = response.data;
          totalCount = response.total;
        }

        const formattedData = datasetsData.map((dataset: Dataset) => ({
          ...dataset,
          created_at: formatDate(dataset.created_at),
          modified_at: formatDate(dataset.modified_at),
        }));

        setDatasets(formattedData);
        setTotalRows(totalCount);
      } catch (error) {
        console.error("Error fetching datasets:", error);
        setError("Failed to fetch datasets");
      } finally {
        setLoading(false);
      }
    };

    getDatasets();
  }, []);

  useEffect(() => {
    const getDatasource = async () => {
      try {
        const response = await fetchDataSource();
        setDatasource(response.data_source_list)
        // setDatasource(["csv", "datalake"])
      } catch (error) {
        console.error("Error fetching datasource configuration:", error);
        setDatasource(["csv"])
      }
    };
    getDatasource();
  }, []);

  const handleUpload = () => {
    if (datasource.length > 1)
      setIsDatasourceModalOpen(true)
    else {
      navigate("/UploadDataSet", {
        state: {
          datasource: datasource?.[0]
        }
      })
    }
  };

  const handleViewFiles = (dataset: Dataset) => {
    navigate(`/files/${dataset.id}`, {
      state: {
        templateName: dataset.template_name,
        datasetName: dataset.dataset_name,
        usage: dataset.usage
      }
    });
  };

  const handlePreviewFiles = (dataset: Dataset) => {
    navigate(`/dataset-view/${dataset.id}`, {
      state: {
        templateName: dataset.template_name,
        datasetName: dataset.dataset_name,
        usage: dataset.usage,
        datasource: dataset.data_source
      }
    });
  };
  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    id: number
  ) => {
    setMenuAnchor((prev) => ({ ...prev, [id]: event.currentTarget }));
  };

  const handleMenuClose = (id: number) => {
    setMenuAnchor((prev) => ({ ...prev, [id]: null }));
  };

  const handleDeleteClick = (dataset: Dataset) => {
    setItemToDelete(dataset);
    setIsDeleteModalOpen(true);
    handleMenuClose(dataset.id);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setDeleteLoading(true);
    try {
      await handleDeleteDataset(itemToDelete.id);

      const skip = pagination.pageIndex * pagination.pageSize;
      const limit = pagination.pageSize;
      const page = pagination.pageIndex + 1;
      const response = await handleFetchDatasets(skip, limit, page);

      let datasetsData: Dataset[] = [];
      let totalCount = 0;

      if (Array.isArray(response)) {
        datasetsData = response;
        totalCount = response.length;
      } else if (
        response &&
        typeof response === "object" &&
        "data" in response &&
        "total" in response
      ) {
        datasetsData = response.data;
        totalCount = response.total;
      }

      const formattedData = datasetsData.map((dataset: Dataset) => ({
        ...dataset,
        created_at: formatDate(dataset.created_at),
        modified_at: formatDate(dataset.modified_at),
      }));

      setDatasets(formattedData);
      setTotalRows(totalCount);
      setIsDeleteModalOpen(false);
      setItemToDelete(null);
      toast.success("Dataset deleted successfully");
    } catch (error: any) {
      setIsDeleteModalOpen(false);
      toast.error(
        `Failed to delete ${itemToDelete.dataset_name}. ${error.message}`
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDownload = async (errorReportPath: string | null) => {
    if (!errorReportPath) {
      toast.error("No error report available");
      return;
    }

    try {
      const response = await handleDownloadError(errorReportPath);
      const url = window.URL.createObjectURL(new Blob([response]));
      const link = document.createElement("a");
      link.href = url;

      const filename = errorReportPath.split("/").pop() || "error_report.csv";
      link.setAttribute("download", filename);

      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download error report");
    }
  };

  const columns = useMemo<MRT_ColumnDef<Dataset>[]>(
    () => [
      {
        accessorKey: "actions",
        header: " ",
        enableSorting: false,
        enableColumnFilter: false,
        enableColumnActions: false,
        size: 20,
        Cell: ({ row }) => (
          <>
            <IconButton onClick={(e) => handleMenuClick(e, row.original.id)}>
              <MoreVert />
            </IconButton>
            <Menu
              anchorEl={menuAnchor[row.original.id]}
              open={Boolean(menuAnchor[row.original.id])}
              onClose={() => handleMenuClose(row.original.id)}
              sx={{
                "& .MuiPaper-root": {
                  backgroundColor: "#fff",
                  width: "105px",
                },
              }}
            >
              <MenuItem
                onClick={() => handlePreviewFiles(row.original)}
                sx={{
                  color: "black",
                  paddingBottom: "15px",
                  "&:hover": { backgroundColor: "#f4f4f4ff" },
                }}
              >
                <RiEyeLine size={18} />&nbsp;&nbsp;View
              </MenuItem>
              <MenuItem
                onClick={() => handleDeleteClick(row.original)}
                sx={{
                  color: "black",
                  paddingBottom: "15px",
                  "&:hover": { backgroundColor: "#f4f4f4ff" },
                }}
              >
                <RiDeleteBin5Line size={18} />&nbsp;&nbsp;Delete
              </MenuItem>
            </Menu>
          </>
        ),
      },
      { accessorKey: "dataset_name", header: "Dataset Name" },
      {
        header: "Template Name",
        accessorFn: (row) => `${row.template_name || row.iceberg_table_name}`,
        // size:200
      },
      {
        header: "Data Source",
        accessorFn: (row) => `${row.data_source === "datalake" ? "Datalake" : "CSV"}`,
        // size: 200
      },
      {
        header: "Usage",
        accessorFn: (row) => `${row?.usage === "t" ? "Training" : row?.usage === "p" ? "Prediction" : "-"}`,
        // size: 150
      },
      { accessorKey: "created_at", header: "Date Created" },
      { accessorKey: "modified_at", header: "Date Modified" },
      {
        accessorKey: "total_fields",
        header: "No. of Files",
        Cell: ({ row }) => {
          const fileCount = row.original.file_count;
          return (
            <span>
              {fileCount > 0 && `${fileCount} `}
              <button
                onClick={() => {
                  if (row.original.data_source !== "datalake") {
                    if (fileCount > 0) {
                      handleViewFiles(row.original);
                    } else {
                      handleUploadFiles(row.original);
                    }
                  }
                }}
                className={`${row.original.data_source === "datalake" ? "cursor-auto" : "text-blue-500 ml-1 hover:underline cursor-pointer"}`}
              >
                {row.original.data_source === "datalake" ? '-' : fileCount > 0 ? "View files" : "Upload files"}
              </button>
            </span>
          );
        },
      },
      {
        accessorKey: "is_valid",
        header: "Template Validation",
        Cell: ({ row, cell }) => {
          const fileCount = row.original.file_count;
          const datasource = row.original.data_source;
          let statusValue = cell.getValue().toString();

          if (fileCount === 0 && datasource !== "datalake") {
            return <StatusBadge value={"false"} isCustomerTag={false} />;
          }

          return statusValue ? <StatusBadge value={statusValue} isCustomerTag={false} /> : "-";
        },
      },
      {
        accessorKey: "dataset_usability",
        header: "Dataset Usability",
        Cell: ({ row, cell }) => {
          const fileCount = row.original.file_count;
          let statusValue = cell.getValue().toString() === "true" ? "Ready" :"Not Ready";
          const datasource = row.original.data_source;

          if (fileCount === 0 && datasource !== "datalake") {
            return <StatusBadge value={"Not Ready"} isCustomerTag={false} />;
          }

          return statusValue ? <StatusBadge value={statusValue} isCustomerTag={false} /> : "-";
        },
      },
    ],
    [menuAnchor]
  );



  return (
    <>
      <BreadCrumb moduleName={"Dataset"} pageName={"Dataset List"} />
      <ToastContainer position="top-center" />
      <div className="d-flex flex-sm-row flex-column w-100 justify-content-between align-items-lg-center gap-2 mb-1 mt-0">
        <div className="d-flex flex-row gap-2">
          <h5 className="rv-page-title p-2">Datasets List</h5>
        </div>
        <div className="d-flex align-items-center">
          <button
            type="button"
            onClick={handleUpload}
            className="btn-label rv-primary-btn me-2 rounded-1 px-4 py-1 text-nowrap btn btn-secondary"
          >
            <i className="ri-add-line label-icon align-middle fs-16 me-1"></i>
            <span className="ps-3 ms-2 me-1">Add Dataset</span>
          </button>
        </div>
      </div>

      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        itemType={itemToDelete?.type || "Dataset"}
        itemName={itemToDelete?.dataset_name}
        loading={deleteLoading}
      />

      <DatasourceModal open={isDatasourceModalOpen} setOpen={setIsDatasourceModalOpen} />

      <div className="container-fluid px-0">
        <div className="disable-margin-card table-responsive table-customer border table-border w-100">
          <ReusableTable
            columns={columns}
            data={datasets}
            loading={loading}
            error={error}
            pagination={pagination}
            onPaginationChange={setPagination}
          />
        </div>
      </div>
    </>
  );
};

export default DataList;